export interface MetricDefinition {
  label: string;
  formula: string;
  description: string;
}

export const METRIC_DEFINITIONS: Record<string, MetricDefinition> = {
  // Income metrics
  gpi: {
    label: 'GPI',
    formula: 'Units \u00d7 Rent/Unit \u00d7 12',
    description: 'Gross Potential Income \u2014 total annual rent if every unit is occupied at market rate',
  },
  egi: {
    label: 'EGI',
    formula: 'GPI \u2212 Vacancy Loss',
    description: 'Effective Gross Income \u2014 actual collectible income after vacancy and credit loss',
  },
  noi: {
    label: 'NOI',
    formula: 'EGI \u2212 Operating Expenses',
    description: 'Net Operating Income \u2014 what the property earns after all operating costs, before debt service',
  },
  stabilized_noi: {
    label: 'Stabilized NOI',
    formula: 'EGI (full occupancy) \u2212 OpEx',
    description: 'NOI assuming all units leased at current market rents with no renovation',
  },
  uplifted_noi: {
    label: 'Uplifted NOI',
    formula: 'EGI (post-reno rents) \u2212 OpEx',
    description: 'NOI after renovation \u2014 all units leased at post-renovation market rents',
  },

  // Cap rates
  entry_cap: {
    label: 'Entry Cap Rate',
    formula: 'Stabilized NOI \u00f7 Purchase Price',
    description: 'Current yield on the purchase price \u2014 higher is better for buyers',
  },
  exit_cap: {
    label: 'Exit Cap Rate',
    formula: 'Forward NOI \u00f7 Sale Price',
    description: 'Assumed buyer cap rate at sale \u2014 lower exit cap means higher sale price',
  },
  yield_on_cost: {
    label: 'Yield on Cost',
    formula: 'Uplifted NOI \u00f7 Total Investment',
    description: 'Return on total capital deployed (purchase + CapEx) \u2014 the true value-add metric',
  },

  // Returns
  coc: {
    label: 'Cash-on-Cash',
    formula: 'Year 1 Cash Flow \u00f7 Equity Invested',
    description: 'First-year cash return on your equity \u2014 the simplest return measure',
  },
  after_tax_coc: {
    label: 'After-Tax CoC',
    formula: '(Cash Flow + Tax Benefit) \u00f7 Equity',
    description: 'Cash-on-cash including depreciation tax savings \u2014 your actual take-home yield',
  },
  irr: {
    label: 'IRR',
    formula: 'Rate where NPV of all cash flows = 0',
    description: 'Internal Rate of Return \u2014 annualized return accounting for timing of every cash flow including exit',
  },
  after_tax_irr: {
    label: 'After-Tax IRR',
    formula: 'IRR including tax benefits and exit taxes',
    description: 'IRR with depreciation savings added and exit taxes deducted \u2014 your true annualized return',
  },
  equity_multiple: {
    label: 'Equity Multiple',
    formula: 'Total Return \u00f7 Equity Invested',
    description: 'How many times you get your money back \u2014 2.0x means you doubled your investment',
  },
  cumulative_return: {
    label: 'Cumulative Return',
    formula: 'Sum of all after-tax cash flows',
    description: 'Total dollars received over the hold period before exit proceeds',
  },

  // Debt metrics
  dscr: {
    label: 'DSCR',
    formula: 'NOI \u00f7 Annual Debt Service',
    description: 'Debt Service Coverage Ratio \u2014 lenders require \u22651.25x (NOI covers 125% of loan payments)',
  },
  ltv: {
    label: 'LTV',
    formula: 'Loan Amount \u00f7 Total Financed',
    description: 'Loan-to-Value \u2014 what percentage of the deal is financed by debt (max 70%)',
  },

  // Tax concepts
  obbb: {
    label: 'OBBB Bonus',
    formula: '100% first-year depreciation on eligible CapEx',
    description: 'One Big Beautiful Bill Act \u2014 allows immediate expensing of renovation costs in Year 1',
  },
  exchange_1031: {
    label: '1031 Exchange',
    formula: 'Defer capital gains by reinvesting in like-kind property',
    description: 'Tax-deferred sale \u2014 defer all exit taxes by purchasing a replacement property within 180 days',
  },
  depr_recapture: {
    label: 'Depreciation Recapture',
    formula: 'Cumulative Depreciation \u00d7 25%',
    description: 'Tax owed at sale on previously claimed depreciation deductions \u2014 25% federal rate',
  },
  capital_gains: {
    label: 'Capital Gains Tax',
    formula: '(Sale Price \u2212 Adjusted Basis) \u00d7 23.8%',
    description: 'Federal tax on property appreciation \u2014 20% long-term rate + 3.8% NIIT',
  },

  // Capital stack
  capex: {
    label: 'CapEx',
    formula: 'Per-unit budget \u00d7 Units',
    description: 'Capital Expenditure \u2014 renovation budget sized to achieve target rent uplift (20% ROI target)',
  },
  opex_ratio: {
    label: 'OpEx Ratio',
    formula: 'Total OpEx \u00f7 EGI',
    description: 'Operating expense ratio as percentage of Effective Gross Income \u2014 tier-based from SSOT config',
  },

  // Waterfall-specific
  vacancy_loss: {
    label: 'Vacancy Loss',
    formula: 'GPI \u00d7 Vacancy Rate',
    description: 'Income lost to vacant units \u2014 percentage of gross potential income not collected',
  },
  debt_service: {
    label: 'Debt Service',
    formula: 'Monthly P&I \u00d7 12',
    description: 'Annual mortgage payments (principal + interest) on the permanent loan',
  },
  pre_tax_cf: {
    label: 'Pre-Tax Cash Flow',
    formula: 'NOI \u2212 Debt Service',
    description: 'Cash remaining after paying operating expenses and mortgage \u2014 before tax adjustments',
  },
  after_tax_cf: {
    label: 'After-Tax Cash Flow',
    formula: 'Pre-Tax CF + Tax Benefits',
    description: 'Actual cash in your pocket after depreciation tax savings are applied',
  },
  equity_invested: {
    label: 'Equity Invested',
    formula: 'Down Payment + Closing + CapEx',
    description: 'Total cash you put into the deal \u2014 the denominator for all return calculations',
  },
};
